<template>
  <div>
    <div class="category-setting">
      <h4>{{ categorySetting.description }}</h4>
      <Tooltip :disabled="!isDisabled('all')">
        <template #trigger>
          <ToggleSwitch
            :modelValue="displayValue('all')"
            :disabled="isDisabled('all')"
            @update:modelValue="updateValue('enabled', $event)"
          />
        </template>
        <template #tooltip>
          <span>{{ disabledTooltip("all") }}</span>
        </template>
      </Tooltip>
    </div>

    <div
      v-for="format in categorySetting.supported_formats"
      :key="format"
      class="format-setting"
    >
      <div class="format-setting__icon-wrapper">
        <i :class="formatConfig[format].icon"></i>
      </div>
      <div>{{ formatConfig[format].name }}</div>
      <div class="format-setting__switch">
        <Tooltip :disabled="!isDisabled(format)">
          <template #trigger>
            <ToggleSwitch
              :modelValue="displayValue(format)"
              :disabled="isDisabled(format)"
              @update:modelValue="updateValue(format, $event)"
            />
          </template>
          <template #tooltip>
            <span>{{ disabledTooltip(format) }}</span>
          </template>
        </Tooltip>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";

import ToggleSwitch from "@/components/ToggleSwitch.vue";
import Tooltip from "@/components/Tooltip.vue";

const formatConfig = {
  email: { name: "Email", icon: "fa-regular fa-envelope" },
  sms: { name: "Text", icon: "fa-regular fa-message-lines" },
  push: { name: "App notification", icon: "fa-regular fa-message-exclamation" }
};

const props = defineProps({
  categorySetting: {
    type: Object,
    required: true
  },
  communicationSettings: {
    type: Object,
    required: true
  }
});

const currentValues = ref({
  enabled: props.categorySetting.enabled,
  sms: props.categorySetting.sms,
  email: props.categorySetting.email,
  push: props.categorySetting.push
});

const emit = defineEmits(["change"]);

function updateValue(key, value) {
  currentValues.value[key] = value;
  emit("change", {
    category: props.categorySetting.category,
    key: props.categorySetting.key,
    ...currentValues.value
  });
}

function isDisabled(format) {
  return props.communicationSettings.isDisabled({
    category: props.categorySetting.category,
    key: props.categorySetting.key,
    format
  });
}

function displayValue(format) {
  return props.communicationSettings.displayValue({
    category: props.categorySetting.category,
    key: props.categorySetting.key,
    format
  });
}

function disabledTooltip(format) {
  if (displayValue(format)) {
    return "Required";
  } else {
    return "Disabled by a higher level setting";
  }
}
</script>

<style lang="scss" scoped>
.category-setting {
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 1rem;
  padding-right: 1.25rem;
}
.format-setting {
  display: flex;
  align-items: center;
  padding: 10px 0px;
  padding-right: 1.25rem;
  border-top: 1px solid var(--gray-border-color);

  &__icon-wrapper {
    display: flex;
    width: 24px;
    font-size: 16px;
  }

  &__switch {
    margin-left: auto;
  }
}
</style>
